import React, { useEffect, useState } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  Button,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  IconButton,
  Popover,
} from "@mui/material";
import dayjs from "dayjs";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import { BiImport } from "react-icons/bi";
import { CiEdit } from "react-icons/ci";
import { GoTrash } from "react-icons/go";
import DeleteModal from "../../components/Model/DeleteModal";
import Modal from "@mui/material/Modal";
import AddEditHarvestImportTags from "../../components/HarvestImportTags/AddEditHarvestImportTags";

const HarvestImportTags = () => {
  const [assignedFilter, setAssignedFilter] = useState("select");
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  // selection state
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedId, setSelectedId] = useState([]);
  const [allIds, setAllIds] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [rowCount, setRowCount] = useState(0);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [showImportMessageModal, setShowImportMessageModal] = useState(false);

  // Popover action state
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionRowId, setActionRowId] = useState(null);
  const [openSingleDeleteModal, setOpenSingleDeleteModal] = useState(false);
  const [singleDeleteId, setSingleDeleteId] = useState(null);

  const httpService = new HttpService();

  const fetchTags = async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search,
      };
      if (assignedFilter !== "All" && assignedFilter !== "select") {
        params.assigned = assignedFilter === "Yes";
      }
      const res = await httpService.get("harvestimport", params);
      const list = res?.data?.data || res?.data || [];
      setRowCount(res?.data?.pagination?.total || 0);
      const mapped = Array.isArray(list)
        ? list.map((row, idx) => ({
            id: row.id || idx + 1,
            tag: row.tag || "",
            type: row.type || "",
            status: row.status || "",
            commissioned: row.commissioned
              ? dayjs(row.commissioned).format("DD-MM-YYYY")
              : "",
            stage: !!row.stage,
            date_of_import: row.date_of_import
              ? dayjs(row.date_of_import).format("DD-MM-YYYY")
              : "",
            is_assigned: !!row.is_assigned,
          }))
        : [];
      setTags(mapped);
    } catch (e) {
      setTags([]);
      setRowCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTags();
  }, [paginationModel.page, paginationModel.pageSize, search, assignedFilter]);

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await httpService.post("harvestimport/upload", formData);
      if (response?.success) {
        toast.success("File imported successfully");
        fetchTags();
      }
      setImportMessage(
        response?.message ||
          (response?.success ? "File imported successfully" : "Import failed"),
      );
      setShowImportMessageModal(true);
    } catch (error) {
      setImportMessage("Import failed. Please try again.");
      setShowImportMessageModal(true);
      toast.error("Import failed. Please try again.");
      console.error("Import failed:", error);
    }
  };

  const handleDeleteMultiple = async () => {
    if (!selectedId || selectedId.length === 0) return;
    setLoading(true);
    try {
      const res = await httpService.post("harvestimport/deleteMultiple", {
        ids: selectedId,
      });
      if (res?.success) {
        toast.success(res?.message || "Deleted successfully");
        setSelectedId([]);
        setIsAllSelected(false);
        setAllIds(0);
        fetchTags();
      } else {
        toast.error(res?.message || "Delete failed.");
      }
    } catch (e) {
      toast.error("Delete failed.");
    }
    setLoading(false);
    setOpenDeleteModal(false);
  };

  const handleDeleteSingle = async () => {
    if (!singleDeleteId) return;
    setLoading(true);
    try {
      const res = await httpService.delete(`harvestimport/${singleDeleteId}`);
      if (res?.success) {
        toast.success(res?.message || "Deleted successfully");
        fetchTags();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (e) {
      toast.error("Delete failed");
    }
    setLoading(false);
    setOpenSingleDeleteModal(false);
    setSingleDeleteId(null);
  };

  const isIdSelected = (id) => {
    return selectedId?.includes(id);
  };

  const toggleId = (id) => {
    setSelectedId((prev = []) => {
      if (prev.includes(id)) {
        const updated = prev.filter((item) => item !== id);
        if (updated.length < allIds) {
          setIsAllSelected(false);
        }
        return updated;
      } else {
        const updated = [...prev, id];
        if (updated.length === allIds) {
          setIsAllSelected(true);
        }
        return updated;
      }
    });
  };

  const handleHeaderCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const ids = tags?.map((row) => row.id) || [];
      setSelectedId(ids);
      setAllIds(ids.length);
    } else {
      setSelectedId([]);
      setAllIds(0);
    }
    setIsAllSelected(isChecked);
  };

  const handleEdit = (tag) => {
    // Format dates back to YYYY-MM-DD for form input fields
    const formattedData = {
      ...tag,
      commissioned: tag.commissioned
        ? dayjs(tag.commissioned, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "",
      date_of_import: tag.date_of_import
        ? dayjs(tag.date_of_import, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "",
    };
    setEditData(formattedData);
    setShowEditModal(true);
  };

  const handleUpdate = async (updatedTag) => {
    setLoading(true);
    try {
      const res = await httpService.put(`harvestimport/${updatedTag.id}`, {
        tag: updatedTag.tag,
        type: updatedTag.type,
        status: updatedTag.status,
        commissioned: updatedTag.commissioned,
        stage: updatedTag.stage,
        date_of_import: updatedTag.date_of_import,
        is_assigned: updatedTag.is_assigned,
      });
      if (res?.success) {
        toast.success(res?.message || "Updated successfully");
        fetchTags();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (e) {
      toast.error("Update failed");
    }
    setLoading(false);
    setEditData(null);
  };

  const handleOpenDeletePrompt = () => {
    if (!selectedId || selectedId.length === 0) {
      toast.error("Please select at least one record to delete");
      return;
    }
    setOpenDeleteModal(true);
  };

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setActionRowId(rowId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActionRowId(null);
  };

  const columns = [
    // {
    //   field: "action",
    //   headerName: "Action",
    //   align: "center",
    //   width: 100,
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params) => (
    //     <>
    //       <div className="action_icon-weap">
    //         <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
    //           <GridMoreVertIcon />
    //         </IconButton>
    //       </div>
    //       {anchorEl && actionRowId === params.row.id && (
    //         <Popover
    //           className="popup-menu"
    //           open={Boolean(anchorEl)}
    //           anchorEl={anchorEl}
    //           onClose={handleCloseMenu}
    //           anchorOrigin={{
    //             vertical: "bottom",
    //             horizontal: "right",
    //           }}
    //           transformOrigin={{
    //             vertical: "top",
    //             horizontal: "right",
    //           }}
    //         >
    //           <MenuItem
    //             onClick={() => {
    //               handleEdit(params.row);
    //               handleCloseMenu();
    //             }}
    //             className="menu-list-items"
    //           >
    //             <div className="menu-list-items-text">Edit</div>
    //             <CiEdit style={{ cursor: "pointer" }} />
    //           </MenuItem>
    //           <MenuItem
    //             onClick={() => {
    //               setSingleDeleteId(params.row.id);
    //               setOpenSingleDeleteModal(true);
    //               handleCloseMenu();
    //             }}
    //             className="menu-list-items"
    //           >
    //             <div className="menu-list-items-text">Delete</div>
    //             <GoTrash style={{ cursor: "pointer" }} />
    //           </MenuItem>
    //         </Popover>
    //       )}
    //     </>
    //   ),
    // },
    {
      field: "select_All",
      headerName: "Select All",
      minWidth: 150,
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Checkbox
            value="Checked"
            checked={
              isAllSelected ? true : isIdSelected(params.row.id) ? true : false
            }
            onChange={() => toggleId(params.row.id)}
          />
        );
      },
      renderHeader: () => {
        return (
          <>
            <Checkbox
              checked={isAllSelected}
              onChange={(e) => handleHeaderCheckboxChange(e)}
            />
            <strong>Select All</strong>
          </>
        );
      },
    },
    { field: "tag", headerName: "Tags", flex: 1, minWidth: 300 },
    { field: "type", headerName: "Type", flex: 1, minWidth: 120 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 120 },
    {
      field: "commissioned",
      headerName: "Commissioned",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "stage",
      headerName: "Staged",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "date_of_import",
      headerName: "Date of Import",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "is_assigned",
      headerName: "Assigned",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
  ];

  return (
    <div className="clone">
      <div className="page-title">
        <div className="title-wrap">
          <h1>Import Tags</h1>
        </div>
        <div className="button-wrap" style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <BiImport width={16} height={16} /> Import
          </button>
        </div>
      </div>

      <div className="table-filter-wrap d-flex justify-content-between pb-0">
        <div className="header_search-wrap">
          <div className="header_search_bar common_content d-flex">
            <div className="input-group input_group">
              <button className="btn search-btn search_btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 21 21"
                  fill="none"
                >
                  <circle
                    cx="9.98856"
                    cy="9.98856"
                    r="8.98856"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.2402 16.7071L19.7643 20.222"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                type="search"
                className="form-control form_control"
                placeholder="Search..."
                aria-label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="pt-2 d-flex align-items-center justify-content-between">
        <Button className="btn btn-delete" onClick={handleOpenDeletePrompt}>
          Delete
        </Button>
        <div className="d-flex align-items-center" style={{ gap: "16px" }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <label
              id="assigned-filter-label"
              style={{
                marginBottom: "4px",
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#495057",
              }}
            >
              Assigned
            </label>
            <Select
              labelId="assigned-filter-label"
              id="assigned-filter-select"
              value={assignedFilter}
              displayEmpty
              onChange={(e) => setAssignedFilter(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  paddingY: "7px",
                  backgroundColor: "#fff",
                },
              }}
            >
              <MenuItem value="select" selected disabled>
                Select
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <div style={{ width: "100%", marginTop: 24 }}>
        <DataGrid
          rows={tags}
          columns={columns}
          getRowHeight={() => "auto"}
          loading={loading}
          sx={{
            border: "1px solid #E0E0E0",
            [`& .${gridClasses.cell}`]: { py: 0.5 },
          }}
          autoHeight
          showCellVerticalBorder
          showColumnVerticalBorder
          pagination
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[15, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </div>

      <AddEditHarvestImportTags
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditData(null);
        }}
        editData={editData}
        onUpdate={handleUpdate}
        showImportModal={showModal}
        onCloseImportModal={() => setShowModal(false)}
        onImport={handleImport}
      />

      {showImportMessageModal && (
        <Modal
          open={showImportMessageModal}
          onClose={() => {
            setShowImportMessageModal(false);
            setImportMessage("");
          }}
          aria-labelledby="import-message-modal-title"
          aria-describedby="import-message-modal-desc"
        >
          <div
            style={{
              padding: 32,
              background: "#fff",
              borderRadius: 8,
              maxWidth: 420,
              margin: "120px auto",
              outline: "none",
              boxShadow: "0 8px 32px rgba(20,40,90,.16)",
              textAlign: "center",
            }}
          >
            <h2
              id="import-message-modal-title"
              style={{ marginBottom: 16, fontSize: 20 }}
            >
              Import Harvest Tags
            </h2>
            <div
              id="import-message-modal-desc"
              style={{ marginBottom: 28, fontSize: 16, color: "#1976d2" }}
            >
              {importMessage
                ? importMessage.split(",").map((line, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom:
                          index < importMessage.split(",").length - 1 ? 8 : 0,
                      }}
                    >
                      {line.trim()}
                    </div>
                  ))
                : importMessage}
            </div>
            <button
              className="btn btn-primary"
              style={{ minWidth: 120 }}
              onClick={() => {
                setShowImportMessageModal(false);
                setImportMessage("");
              }}
            >
              OK
            </button>
          </div>
        </Modal>
      )}

      {openDeleteModal && (
        <DeleteModal
          open={openDeleteModal}
          HandleClose={() => setOpenDeleteModal(false)}
          HandleDelete={handleDeleteMultiple}
        />
      )}

      {openSingleDeleteModal && (
        <DeleteModal
          open={openSingleDeleteModal}
          HandleClose={() => {
            setOpenSingleDeleteModal(false);
            setSingleDeleteId(null);
          }}
          HandleDelete={handleDeleteSingle}
        />
      )}
    </div>
  );
};

export default HarvestImportTags;
